import {
  Button,
  Stack,
  TextField,
  Typography,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import useDialog from '../hooks/useDialog';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import type { OverridableStringUnion } from '@mui/types';
import { Controller, useForm } from 'react-hook-form';
import { processWithdrawalRequest } from '../fetchFns';

interface ApproveWithdrawalRequestDialogProps {
  handleUpdateWithdrawalRequest: (
    id: number,
    newStatus: 'pending' | 'completed' | 'rejected',
    newAmount: number,
    newRemarks: string | undefined,
  ) => void;
  amount: number;
  withdrawalRequestId: number;
  newStatus: 'completed' | 'rejected';
}

type FormValues = {
  remarks?: string;
};

const defaultValues: FormValues = {
  remarks: '',
};

export default function ProcessWithdrawalRequestDialog({
  handleUpdateWithdrawalRequest,
  withdrawalRequestId,
  amount,
  newStatus,
}: ApproveWithdrawalRequestDialogProps) {
  const backdrop = useBackdrop();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const handleApproveWithdrawalRequest = async (data: FormValues) => {
    let message = `The withdrawal request has been ${
      newStatus === 'completed' ? 'approved' : 'rejected'
    }.`;
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    backdrop.handleBackdropOpen();
    try {
      await processWithdrawalRequest(
        withdrawalRequestId,
        newStatus,
        data.remarks,
      );
      reset();
      handleUpdateWithdrawalRequest(
        withdrawalRequestId,
        newStatus,
        amount,
        data.remarks,
      );
      dialog.handleDialogClose();
    } catch (error) {
      const errorCast = error as Error;
      message = errorCast.message;
      severity = 'error';
      console.error(errorCast.stack);
    }
    backdrop.handleBackdropClose();
    snackbar.handleSnackbarOpen(message, severity);
  };

  return (
    <Stack
      component="form"
      direction="column"
      spacing={2}
      onSubmit={handleSubmit(handleApproveWithdrawalRequest)}>
      <Controller
        name="remarks"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Remarks"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
          />
        )}
      />
      <Stack justifyContent="end" direction="row" spacing={2}>
        <Button
          onClick={() => {
            dialog.handleDialogClose();
          }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          color={newStatus === 'completed' ? 'primary' : 'error'}>
          {newStatus === 'completed' ? 'Approve' : 'Reject'}
        </Button>
      </Stack>
    </Stack>
  );
}
